import {
  AsYouType,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

import { isSupportedCountry } from "@/data/country";
import type { SupportedCountry } from "@/types/onboarding";

export interface PhoneAnalysis {
  /** ISO-3166 alpha-2 resolved by libphonenumber-js, not string matching. */
  country: CountryCode | null;
  /** True when the country has a curated dataset in src/data/country. */
  supported: boolean;
  valid: boolean;
  e164: string | null;
  formatted: string;
}

/**
 * Resolves the country of a phone number. `fallback` is the country the doctor
 * picked explicitly in the UI and is used when the number has no calling code.
 */
export function analysePhone(input: string, fallback?: CountryCode | undefined): PhoneAnalysis {
  const value = input.trim();
  if (!value) {
    return {
      country: fallback ?? null,
      supported: isSupportedCountry(fallback),
      valid: false,
      e164: null,
      formatted: "",
    };
  }

  const parsed = fallback
    ? parsePhoneNumberFromString(value, fallback)
    : parsePhoneNumberFromString(value);

  const country = parsed?.country ?? fallback ?? null;

  return {
    country,
    supported: isSupportedCountry(country),
    valid: parsed ? parsed.isValid() : false,
    e164: parsed?.number ?? null,
    formatted: new AsYouType(country ?? undefined).input(value),
  };
}

export function isValidPhoneForCountry(value: string, country: CountryCode | undefined): boolean {
  if (!value.trim()) return false;
  try {
    return country ? isValidPhoneNumber(value, country) : isValidPhoneNumber(value);
  } catch {
    return false;
  }
}

export function dialCodeFor(country: SupportedCountry | CountryCode): string {
  try {
    return `+${getCountryCallingCode(country as CountryCode)}`;
  } catch {
    return "";
  }
}
